import { useState, useRef, useEffect } from "preact/hooks"
import { searchDeductions, DeductionSearchResult, type DeductionKind } from "@/api/deduction"
import { format_transaction_amount } from "@/utils"
import { Search, X, FileText } from "lucide-react"

export interface AttachedDeduction {
  id: string
  invoice_number: string
  invoice_amount: number
  kind?: DeductionKind
  source: string
  retailer_name?: string
  category: string
}

interface DeductionSearchBarProps {
  onSelect: (deduction: AttachedDeduction) => void
  /** When a deduction is already selected, the search bar is hidden */
  selectedDeduction: AttachedDeduction | null
  onClear: () => void
  className?: string
}

/** Inline search bar for finding and attaching a deduction to the conversation */
export function DeductionSearchBar({ onSelect, selectedDeduction, onClear, className }: DeductionSearchBarProps) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<DeductionSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Close dropdown on click outside
  useEffect(() => {
    if (!isFocused) return
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsFocused(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [isFocused])

  function handleQueryChange(e: Event) {
    const value = (e.target as HTMLInputElement).value
    setQuery(value)

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!value.trim()) {
      setResults([])
      return
    }

    setIsSearching(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchDeductions({ q: value.trim() })
        setResults(data)
      } catch {
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }, 250)
  }

  function handleSelect(result: DeductionSearchResult) {
    onSelect({
      id: result.id,
      invoice_number: result.invoice_number,
      invoice_amount: result.invoice_amount,
      kind: result.kind,
      source: result.source,
      retailer_name: result.retailer_name,
      category: result.category,
    })
    setQuery("")
    setResults([])
    setIsFocused(false)
  }

  // If a deduction is selected, show the chip instead of the search
  if (selectedDeduction) {
    return (
      <div class={className || ""}>
        <DeductionChip deduction={selectedDeduction} onRemove={onClear} />
      </div>
    )
  }

  const showDropdown = isFocused && query.trim().length > 0

  return (
    <div ref={containerRef} class={`relative ${className || ""}`}>
      <div class="flex items-center gap-2">
        <div class="shrink-0 p-1.5">
          <Search className="w-5 h-5 text-gray-300" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onInput={handleQueryChange}
          onFocus={() => setIsFocused(true)}
          placeholder="Attach a deduction (optional)"
          class="flex-1 text-xs bg-transparent border-0 outline-none placeholder-gray-300 text-gray-600 py-0.5"
        />
        {isSearching && (
          <div class="w-3.5 h-3.5 border-2 border-gray-200 border-t-primary rounded-full animate-spin shrink-0" />
        )}
      </div>

      {showDropdown && (
        <div class="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          <div class="max-h-56 overflow-y-auto">
            {results.length === 0 && !isSearching ? (
              <div class="px-3 py-3 text-xs text-gray-400 text-center">
                No deductions found
              </div>
            ) : (
              results.map((result: DeductionSearchResult) => (
                <button
                  key={result.id}
                  type="button"
                  onClick={() => handleSelect(result)}
                  class="w-full px-3 py-2 flex items-center gap-2.5 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0"
                >
                  <div class="shrink-0 w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center">
                    <FileText className="w-3.5 h-3.5 text-gray-500" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-1.5">
                      <span class="text-sm font-medium text-gray-800 truncate">
                        {result.invoice_number}
                      </span>
                      {result.po_number && (
                        <span class="text-xs text-gray-400">PO: {result.po_number}</span>
                      )}
                    </div>
                    <div class="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                      <span>{result.source}</span>
                      {result.retailer_name && (
                        <>
                          <span class="text-gray-300">|</span>
                          <span class="truncate">{result.retailer_name}</span>
                        </>
                      )}
                      {result.category && (
                        <>
                          <span class="text-gray-300">|</span>
                          <span class="truncate">{result.category}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <span class="shrink-0 text-xs font-medium text-gray-600">
                    {format_transaction_amount(result.kind, result.invoice_amount)}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

interface DeductionChipProps {
  deduction: AttachedDeduction
  onRemove: () => void
  className?: string
}

/** Compact chip showing an attached deduction, with a remove button */
export function DeductionChip({ deduction, onRemove, className }: DeductionChipProps) {
  return (
    <div class={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-0.5 rounded-lg bg-primary/5 border border-primary/20 text-xs ${className || ""}`}>
      <FileText className="w-3 h-3 text-primary shrink-0" />
      <span class="font-medium text-primary">{deduction.invoice_number}</span>
      <span class="text-primary/60">
        {format_transaction_amount(deduction.kind ?? "deduction", deduction.invoice_amount)}
      </span>
      <button
        type="button"
        onClick={onRemove}
        class="p-0.5 rounded hover:bg-primary/10 text-primary/60 hover:text-primary transition-colors"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  )
}
